import { layout } from "@app/domain";
import { useCallback, useEffect, useRef, useState } from "react";
import { type DealCue, useStore } from "../store";
import { FACTS } from "../variants/facts";
import type { AnyView } from "../variants/types";
import { cardLabel } from "./Card";

/** One deal on screen: the flights, how long it runs, which of your cards stay hidden meanwhile. */
export interface DealRun {
  id: number;
  timing: layout.DealTiming;
  flights: layout.DealFlight[];
  duration: number;
  hidden: ReadonlySet<string> | "all";
}

/** A cue older than this is history (the table screen came up late): nothing replays. */
const FRESH_MS = 2_000;

/** The flights for a cue: a full hand, or Baloot's last three onto the places they take in your hand. */
function planRun(
  cue: DealCue,
  view: AnyView,
  seated: boolean,
  before: readonly string[],
  canvas: layout.Canvas,
): DealRun {
  const labels = view.yourHand.map(cardLabel);
  if (cue.kind === "rest") {
    const fresh = labels.flatMap((label, i) => (before.includes(label) ? [] : [i]));
    const request = {
      cardsEach: 3,
      handCount: labels.length,
      landing: seated ? fresh : null,
      timing: layout.REST_DEAL,
    };
    return {
      id: cue.at,
      timing: request.timing,
      flights: layout.dealFlights(canvas, request),
      duration: layout.dealDuration(request),
      hidden: new Set(fresh.map((i) => labels[i] ?? "")),
    };
  }
  const cardsEach = FACTS[view.variant].openingDeal;
  const request = {
    cardsEach,
    handCount: cardsEach,
    landing: seated ? Array.from({ length: cardsEach }, (_, i) => i) : null,
    // Pişti's next four come off the deck mid-game: the same flights, without the wind-up.
    timing: cue.kind === "top-up" ? layout.REST_DEAL : layout.FULL_DEAL,
  };
  return {
    id: cue.at,
    timing: request.timing,
    flights: layout.dealFlights(canvas, request),
    duration: layout.dealDuration(request),
    hidden: "all",
  };
}

/** Starts a deal when the store cues a live one; `done` ends it and gives the hand back. */
export function useDeal(
  view: AnyView,
  seated: boolean,
  canvas: layout.Canvas,
): { run: DealRun | null; done: () => void } {
  const cue = useStore((s) => s.deal);
  const labels = view.yourHand.map(cardLabel);
  // The hand before this render: Baloot's rest is told apart by what is new in it.
  const before = useRef<string[]>(labels);
  const [handled, setHandled] = useState(0);
  const [run, setRun] = useState<DealRun | null>(null);
  if (cue !== null && cue.at !== handled && Date.now() - cue.at < FRESH_MS) {
    setHandled(cue.at);
    setRun(planRun(cue, view, seated, before.current, canvas));
  }
  useEffect(() => {
    before.current = labels;
  });
  const done = useCallback(() => setRun(null), []);
  return { run, done };
}
